import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { container } from '../../../../shared/di/container.js';
import { appFeedback } from '@/shared/feedback/appFeedback';
import {
  deleteWebPushToken,
  onForegroundMessage,
  requestAndGetWebPushToken,
} from '../../../../core/push/webPushService.js';

function routeForData(data: Record<string, string> | undefined): string {
  if (!data) return '/notifications';
  switch (data.resource) {
    case 'issue':
      return `/issues?drawer=${data.resource_id}`;
    case 'session':
      return '/sessions';
    default:
      return '/notifications';
  }
}

/**
 * Registers this browser for FCM web push when a user is logged in, and keeps the
 * notification bell live by invalidating the 'notifications' queries on foreground
 * messages. Cleans up the token on logout. Mount once (in AppShell).
 */
export function useWebPush(userId: string | undefined) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tokenRef = useRef<string | null>(null);
  const prevUserRef = useRef<string | undefined>(undefined);

  // Register + foreground listener while logged in.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let unsubscribe = () => {};

    (async () => {
      const token = await requestAndGetWebPushToken();
      if (cancelled) return;
      if (token) {
        tokenRef.current = token;
        try {
          await container.notification.registerDeviceTokenUseCase({
            userId,
            fcmToken: token,
            deviceType: 'web',
          });
          console.info('[useWebPush] device token saved to user_device_tokens for', userId);
        } catch (err) {
          console.error('[useWebPush] register token failed (user_device_tokens upsert):', err);
        }
      }

      unsubscribe = await onForegroundMessage((payload) => {
        // Tab is focused → OS won't show it; refresh the bell + surface a toast.
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        const data = (payload.data ?? {}) as Record<string, string>;
        appFeedback.notification(
          'info',
          payload.notification?.title ?? data.title ?? 'Thông báo',
          payload.notification?.body ?? data.body,
          { onClick: () => navigate(routeForData(data)) },
        );
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId, queryClient, navigate]);

  // Revoke this browser's token on logout (truthy → falsy transition).
  useEffect(() => {
    if (prevUserRef.current && !userId && tokenRef.current) {
      const token = tokenRef.current;
      tokenRef.current = null;
      void deleteWebPushToken();
      void container.notification.removeDeviceTokenUseCase(token).catch(() => {});
    }
    prevUserRef.current = userId;
  }, [userId]);
}
